import React from 'react';
import useFinancialScope from './useFinancialScope';

/**
 * Says out loud WHICH company's books a Financials screen is showing.
 *
 * There are two ways the scope can surprise someone, and until now only one of
 * them was ever said out loud:
 *
 *  1. **Scope not in force** — the person's office carries no entity, so the
 *     screens fall back to showing every entity. Deliberate (closing the module
 *     over a missing admin link would black out real staff), but a
 *     wider-than-intended view looks exactly like a correct one, so it has to
 *     announce itself.
 *
 *  2. **Scope in force** — the person is being shown ONE company and the rest of
 *     the book is hidden. This was silent, and it is what produced the reported
 *     fault on 2026-09-03: an accountant opened Outgoing Invoices, saw an empty
 *     table, and concluded nothing had been invoiced. An empty list with no
 *     reason is indistinguishable from a broken screen. Their office was
 *     perfectly well configured, so the case-1 warning never fired for them.
 *
 * Mounted on every scoped Financials list, not just the Invoicing dashboard —
 * the narrowing bites on the lists, so the explanation belongs there.
 *
 * Props:
 *   emptyResult — pass `true` when the list this sits above came back with no
 *                 rows. The notice then leads with why the table is empty
 *                 instead of making the reader join the two facts up.
 */
const EntityScopeNotice = ({ emptyResult = false }) => {
  // One shared request per page: the empty state below the table needs the
  // same answer, and asking twice on every list was the obvious next bug.
  const scope = useFinancialScope();

  if (!scope) return null;

  // Group finance and admins genuinely read every company. Nothing is hidden
  // from them, so there is nothing to explain.
  if (scope.allEntities) return null;

  const base = {
    margin: '0 0 1rem',
    padding: '0.85rem 1rem',
    borderRadius: '6px',
    fontSize: '0.9rem',
  };

  if (scope.unresolved) {
    return (
      <div
        role="status"
        style={{
          ...base,
          border: '1px solid #60a5fa',
          borderLeft: '4px solid #1d4ed8',
          background: '#eff6ff',
          color: '#1e3a8a',
        }}
      >
        <strong style={{ marginRight: '0.4rem' }}>Showing every entity.</strong>
        {scope.warning}
      </div>
    );
  }

  if (!scope.entityName) return null;

  // Scope IS in force. Say so — and say it louder when the list came back empty,
  // because that is the moment the silence used to read as "nothing exists".
  return (
    <div
      role="status"
      style={{
        ...base,
        border: '1px solid #cbd5e1',
        borderLeft: `4px solid ${emptyResult ? '#b45309' : '#475569'}`,
        background: emptyResult ? '#fffbeb' : '#f8fafc',
        color: emptyResult ? '#78350f' : '#334155',
      }}
    >
      {emptyResult ? (
        <>
          <strong style={{ marginRight: '0.4rem' }}>
            Nothing here for {scope.entityName}.
          </strong>
          Financials shows you only the records of the entity your office keeps
          the books for. Records raised under any other IMT entity are hidden
          from this list, even when you can see them on the delivery. If you
          expected something here, check the entity it was raised under, or ask
          an administrator whether your office is on the right entity.
        </>
      ) : (
        <>
          <strong style={{ marginRight: '0.4rem' }}>
            Showing {scope.entityName} only.
          </strong>
          Records raised under any other IMT entity are not listed here.
        </>
      )}
    </div>
  );
};

export default EntityScopeNotice;
